<template>
    <v-card color="primary" class="mb-3" flat tile>
        <v-container class="px-7 mt-7 py-0">
            <v-row no-gutters>
                <v-col cols="12" class="text-center performance__avatar mb-2">
                    <user-avatar
                        :size="75"
                        :user="currentUser"
                        v-if="!isFetchingLatest"
                    ></user-avatar>
                    <v-skeleton-loader
                        v-else
                        type="avatar"
                        class="d-flex justify-center"
                    />
                </v-col>
                <v-col cols="12">
                    <p
                        class="ma-0 mb-1 body-1"
                        v-if="!isFetchingLatest"
                        v-html="greetings"
                    ></p>
                    <p v-else>
                        <v-skeleton-loader type="sentences" />
                    </p>
                </v-col>
            </v-row>
        </v-container>

        <v-sheet color="primary darken-1 py-2" tile>
            <v-container class="py-0 px-7">
                <p class="ma-0 body-1" v-if="!isFetchingLatest">
                    <v-icon>mdi-speedometer</v-icon>
                    {{ $vuetify.lang.t('$vuetify.widgets.performance.title') }}
                </p>
                <div v-else class="d-flex align-center">
                    <v-skeleton-loader
                        type="avatar"
                        class="d-inline-flex flex-shrink-0"
                    />
                    <v-skeleton-loader
                        type="text"
                        class="d-inline-flex ml-4"
                        style="width: 100%"
                    />
                </div>
            </v-container>
        </v-sheet>

        <v-container class="pt-3 pb-5 px-7" v-if="!isFetchingLatest">
            <template v-if="statistics">
                <div class="mb-4 subtitle-1 font-weight-bold">
                    {{
                        $vuetify.lang.t(
                            '$vuetify.page_statistics_detail.company_statistics_title'
                        )
                    }}
                    <v-divider></v-divider>
                </div>
                <v-sparkline
                    :value="Object.values(statistics.flat)"
                    :smooth="5"
                    :line-width="4"
                    :padding="5"
                    showLabels
                    class="performance-graph mb-4"
                    color="white"
                    height="50"
                    label-size="15"
                >
                    <template slot="label">|</template>
                </v-sparkline>
                <v-btn
                    color="primary darken-1"
                    :to="toStatistics"
                    block
                    class="mb-4"
                    elevation="0"
                >
                    {{ $vuetify.lang.t('$vuetify.buttons.details_statistics') }}
                </v-btn>

                <v-divider class="mb-4"></v-divider>
            </template>

            <v-expansion-panels class="mb-2" flat id="dashboard-wallet">
                <v-expansion-panel>
                    <v-expansion-panel-header
                        expand-icon="mdi-menu-down"
                        class="body-1 font-weight-bold"
                    >
                        {{
                            $vuetify.lang.t(
                                '$vuetify.widgets.performance.account_balance'
                            )
                        }}:
                        <v-spacer></v-spacer>
                        <template v-if="currentUser && currentUser.wallet">
                            {{ currentUser.wallet.balance | number }}
                        </template>
                        {{ $vuetify.lang.t('$vuetify.points.short') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="mx-n5">
                        <v-divider></v-divider>
                        <widget-transactions
                            class="mb-6"
                            :transactions="latestTransactions"
                            :hide-title="true"
                        ></widget-transactions>
                        <v-divider></v-divider>
                        <v-container class="pb-0 px-7">
                            <v-btn
                                color="white"
                                outlined
                                :to="{ name: 'account' }"
                                block
                                elevation="0"
                            >
                                <v-icon>mdi-chart-line-variant</v-icon>
                                {{
                                    $vuetify.lang.t(
                                        '$vuetify.buttons.all_transactions'
                                    )
                                }}
                            </v-btn>
                        </v-container>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <submit-contract-link-btn :user="currentUser" />
        </v-container>

        <!-- skeleton loading -->
        <v-container v-else class="">
            <v-card flat tile color="primary">
                <v-container color="primary" pa-0 ma-0>
                    <v-row
                        class="ma-0 pa-0 mb-0 pb-0 align-center justify-space-between"
                    >
                        <v-col class="pa-0" cols="7">
                            <v-skeleton-loader type="text" />
                        </v-col>
                        <v-col class="pa-0" cols="4">
                            <v-skeleton-loader type="chip" />
                        </v-col>
                    </v-row>
                    <v-divider class="my-2"></v-divider>
                    <v-row
                        class="ma-0 pa-0 mb-0 pb-0 align-center justify-space-between"
                    >
                        <v-col class="pa-0" cols="7">
                            <v-skeleton-loader type="text" />
                        </v-col>
                        <v-col class="pa-0" cols="4">
                            <v-skeleton-loader type="chip" />
                        </v-col>
                    </v-row>
                </v-container>
            </v-card>
        </v-container>
    </v-card>
</template>

<script>
// components
import SubmitContractLinkBtn from '../../components/SubmitContractLinkBtn';
import UserAvatar from '../../components/UserAvatar';
import WidgetTransactions from '../../components/Widgets/Transactions';
// models
import Transaction from '../../models/Transaction';
import { User } from '../../models';
import OwnerStatistic from '../../models/OwnerStatistic';
// import UserRank from '../../models/UserRank';
// utils
import {
    LOADER_FETCH_LATEST_TRANSACTIONS,
    ROUTE_STATISTIC_DETAIL
} from '../../utils/constants';

export default {
    name: 'widget-performance',
    components: {
        SubmitContractLinkBtn,
        UserAvatar,
        WidgetTransactions
    },
    computed: {
        greetings() {
            if (this.currentUser) {
                const randomGreeting =
                    Math.floor(
                        Math.random() *
                            Object.keys(
                                this.$vuetify.lang.t(
                                    '$vuetify.dashboard-greetings'
                                )
                            ).length
                    ) + 1;
                return this.$vuetify.lang.t(
                    `$vuetify.dashboard-greetings.${randomGreeting}`,
                    this.currentUser.first_name,
                    this.currentUser.last_name
                );
            }
        },
        statistics() {
            return OwnerStatistic.getStatistics();
        },
        currentUser() {
            return User.getCurrent();
        },
        latestTransactions() {
            return Transaction.getLatest();
        },
        isFetchingLatest() {
            return this.$wait.is(LOADER_FETCH_LATEST_TRANSACTIONS);
        },
        toStatistics() {
            return { name: ROUTE_STATISTIC_DETAIL };
        }
    },
    mounted() {
        Transaction.fetchLatest();
    }
};
</script>
